/**
 * One row to place: the copied item → where it lands here (a select) and how it matched
 * (27/10/2026). Compact in a fold — one line, who uses it on hover; full in the status card —
 * a second line with the match, who uses it and Confirm beside a near match, and anything the
 * row carries (e.g. sender IDs). A row with no match nobody has answered wears the warning tone.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';
import { ArrowRight, Check } from 'lucide-react';

import { cn } from '@/lib/utils';

import { MatchBadge, type MatchBadgeSpec } from './MatchBadge';

interface Props {
  /** The copied item's name — the row's accessible name. */
  name: string;
  source: ReactNode;
  select: ReactNode;
  badge: MatchBadgeSpec;
  meta: string;
  /** Under the row — e.g. the gateway's sender IDs. */
  extra?: ReactNode;
  /** A near match: keep it (the person's answer). */
  onConfirm?: () => void;
  /** In a fold: one line. */
  compact?: boolean;
}

const SHOWN = 3;

/** "used by RAW, FTD, Night +2". */
export function usedByLine(usedBy: string[]): string {
  if (usedBy.length === 0) return 'used by nothing that comes';
  const more = usedBy.length > SHOWN ? ` +${usedBy.length - SHOWN}` : '';
  return `used by ${usedBy.slice(0, SHOWN).join(', ')}${more}`;
}

const Confirm: FC<{ onConfirm: () => void }> = ({ onConfirm }) => (
  <button type="button" onClick={onConfirm} className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-warning/50 bg-background px-2.5 text-xs font-medium text-foreground transition-colors hover:border-warning hover:bg-warning/10">
    <Check className="h-3.5 w-3.5 text-warning" aria-hidden />
    Confirm
  </button>
);

const Lands: FC<{ source: ReactNode; select: ReactNode; meta?: string }> = ({ source, select, meta }) => (
  <>
    <span className="flex min-w-0 items-center text-[13px] font-medium" title={meta}>{source}</span>
    <ArrowRight className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" aria-hidden />
    <div className="min-w-0">{select}</div>
  </>
);

const GRID = 'grid min-w-0 grid-cols-1 items-center gap-x-3 gap-y-1.5 sm:grid-cols-[minmax(0,1fr)_14px_minmax(0,1.2fr)]';

export const PickRow: FC<Props> = ({ name, source, select, badge, meta, extra, onConfirm, compact = false }) => {
  const warn = badge.tone === 'warning' || badge.tone === 'check';
  if (compact)
    return (
      <div role="group" aria-label={name} className="flex min-w-0 flex-col gap-1 px-5 py-2">
        <div className={cn(GRID, 'sm:grid-cols-[minmax(0,1fr)_14px_minmax(0,1.2fr)_6.5rem]')}>
          <Lands source={source} select={select} meta={meta} />
          <MatchBadge spec={badge} />
        </div>
        {extra}
      </div>
    );
  return (
    <div role="group" aria-label={name} className={cn('flex min-w-0 flex-col gap-1.5 border-l-2 px-5 py-2.5 duration-300 animate-in fade-in', warn ? 'border-l-warning' : 'border-l-transparent')}>
      <div className={GRID}>
        <Lands source={source} select={select} />
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <MatchBadge spec={badge} />
        <span className="min-w-0 flex-1 truncate text-[11px] text-muted-foreground" title={meta}>· {meta}</span>
        {badge.tone === 'check' && onConfirm ? <Confirm onConfirm={onConfirm} /> : null}
      </div>
      {extra}
    </div>
  );
};
